import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Types
export interface ClientUser {
  id: string;
  email: string;
  name: string | null;
  has_password: boolean;
  client: {
    id: string;
    name: string;
  };
}

interface ClientPortalAuthState {
  user: ClientUser | null;
  loading: boolean;
  sessionToken: string | null;
}

interface ClientPortalAuthContextValue extends ClientPortalAuthState {
  requestMagicLink: (email: string, clientId?: string) => Promise<{ success: boolean; error?: string }>;
  verifyMagicLink: (token: string) => Promise<{ success: boolean; error?: string }>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  setPassword: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const ClientPortalAuthContext = createContext<ClientPortalAuthContextValue | null>(null);

const SESSION_KEY = 'client_portal_session';

export function ClientPortalAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ClientPortalAuthState>({
    user: null,
    loading: true,
    sessionToken: null,
  });
  const { toast } = useToast();

  // Validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      const savedToken = localStorage.getItem(SESSION_KEY);
      if (!savedToken) {
        setState({ user: null, loading: false, sessionToken: null });
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('client-portal-auth/validate-session', {
          body: { session_token: savedToken },
        });

        if (error || !data?.valid) {
          localStorage.removeItem(SESSION_KEY);
          setState({ user: null, loading: false, sessionToken: null });
          return;
        }

        setState({
          user: data.user,
          loading: false,
          sessionToken: savedToken,
        });
      } catch {
        localStorage.removeItem(SESSION_KEY);
        setState({ user: null, loading: false, sessionToken: null });
      }
    };

    validateSession();
  }, []);

  const requestMagicLink = async (email: string, clientId?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('client-portal-auth/request-magic-link', {
        body: { email, client_id: clientId },
      });

      if (error || data?.error) {
        return { success: false, error: data?.error || error?.message || 'Failed to send magic link' };
      }

      toast({
        title: 'Check your email',
        description: 'We sent you a magic link to sign in',
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to send magic link' };
    }
  };

  const verifyMagicLink = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('client-portal-auth/verify-magic-link', {
        body: { token },
      });

      if (error || data?.error) {
        return { success: false, error: data?.error || error?.message || 'Invalid or expired link' };
      }

      localStorage.setItem(SESSION_KEY, data.session_token);
      setState({
        user: data.user,
        loading: false,
        sessionToken: data.session_token,
      });

      toast({
        title: 'Welcome!',
        description: 'You are now signed in',
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to verify magic link' };
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('client-portal-auth/login', {
        body: { email, password },
      });

      if (error || data?.error) {
        return { success: false, error: data?.error || error?.message || 'Invalid credentials' };
      }

      localStorage.setItem(SESSION_KEY, data.session_token);
      setState({
        user: data.user,
        loading: false,
        sessionToken: data.session_token,
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to sign in' };
    }
  };

  const setPassword = async (password: string) => {
    if (!state.sessionToken) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('client-portal-auth/set-password', {
        body: { session_token: state.sessionToken, password },
      });

      if (error || data?.error) {
        return { success: false, error: data?.error || error?.message || 'Failed to set password' };
      }

      // Update user state to reflect password is set
      if (state.user) {
        setState((prev) => ({
          ...prev,
          user: prev.user ? { ...prev.user, has_password: true } : null,
        }));
      }

      toast({
        title: 'Password set',
        description: 'You can now sign in with your email and password',
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to set password' };
    }
  };

  const logout = async () => {
    if (state.sessionToken) {
      await supabase.functions.invoke('client-portal-auth/logout', {
        body: { session_token: state.sessionToken },
      });
    }

    localStorage.removeItem(SESSION_KEY);
    setState({ user: null, loading: false, sessionToken: null });
  };

  return (
    <ClientPortalAuthContext.Provider
      value={{
        ...state,
        requestMagicLink,
        verifyMagicLink,
        login,
        setPassword,
        logout,
      }}
    >
      {children}
    </ClientPortalAuthContext.Provider>
  );
}

export function useClientPortalAuth() {
  const context = useContext(ClientPortalAuthContext);
  if (!context) {
    throw new Error('useClientPortalAuth must be used within a ClientPortalAuthProvider');
  }
  return context;
}

// Hook to get session token for API calls
export function useClientPortalSession() {
  const { sessionToken } = useClientPortalAuth();
  return sessionToken;
}
